import angular from 'angular';
import _ from 'lodash';
import EventEmitter from 'utility/event-emitter';
import { ExtensionsEventsEnum } from '../../../../modules/extensions-framework/sdk.types';
import { updateParticipant } from '../../../../modules/call/actions';
import { StreamType } from '../../../../modules/call/constants';
import { VideoProviderEvents } from '../video-provider-events';
import { RootScopeEvents } from '../../../../common/services/root-scope-types';
export class ActiveStreamService extends EventEmitter {
    constructor(globalUi, $timeout, VideoProviderFactory, $log, $rootScope, $ngRedux) {
        super();
        this.globalUi = globalUi;
        this.$timeout = $timeout;
        this.VideoProviderFactory = VideoProviderFactory;
        this.$log = $log;
        this.$rootScope = $rootScope;
        this.$ngRedux = $ngRedux;
        this.ACTIVE_STREAM_VOLUME_THRESHOLD = 20;
        this.MAX_PATIENTS_IN_GALLERY_VIEW = 9;
    }
    get activeProvider() {
        return this.VideoProviderFactory.activeProvider;
    }
    init() {
        this.activeProvider.on(VideoProviderEvents.SessionConnected, () => {
            this.setCurrentStreamByVolumeInterval = window.setInterval(() => {
                this.setCurrentStreamByVolume();
                this.$rootScope.$evalAsync();
            }, 500);
        });
        this.activeProvider.on(VideoProviderEvents.SessionDisconnected, () => {
            if (Boolean(this.setCurrentStreamByVolumeInterval)) {
                window.clearInterval(this.setCurrentStreamByVolumeInterval);
            }
            this.reset();
        });
        this.activeProvider.on(VideoProviderEvents.StreamCreated, (stream) => {
            void this.$timeout(() => {
                if (stream.type === StreamType.Screen) {
                    this.onScreenSharingActivate();
                    this.update({ pinnedStreamId: stream.id });
                }
                this.update({ activeStreamId: stream.id });
            });
        });
        this.activeProvider.on(VideoProviderEvents.StreamDestroyed, (stream) => {
            if (stream.type === StreamType.Screen) {
                this.onScreenSharingDeactivate();
            }
            this.$rootScope.$evalAsync();
        });
        this.activeProvider.on(VideoProviderEvents.ConnectionCreated, () => {
            this.onParticipantsCountChanged();
        });
        this.activeProvider.on(VideoProviderEvents.ConnectionDestroyed, () => {
            this.onParticipantsCountChanged();
        });
        this.$rootScope.$on(ExtensionsEventsEnum.IS_SHOWING_WHITEBOARD, (_e, value) => {
            this.globalUi.ui.isShowingWhiteboard = value;
            this.updateGalleryViewState();
        });
        this.$rootScope.$on(RootScopeEvents.GLOBAL_UI.SCREEN_SHARING_UPDATED, () => {
            this.updateGalleryViewState();
        });
    }
    reset() {
        this.globalUi.ui.isGalleryViewButtonHidden = false;
        this.update({ pinnedStreamId: null, activeStreamId: null });
        this.$rootScope.$evalAsync();
    }
    getCurrentStreamId() {
        return this.globalUi.ui.pinnedStreamId || this.globalUi.ui.activeStreamId;
    }
    async getCurrentVideoElement() {
        const currentStreamId = this.getCurrentStreamId();
        if (!currentStreamId) {
            return null;
        }
        let videoEl = null;
        if (currentStreamId === 'self') {
            // FIXME:
            const videoElContainer = document.querySelector(`#${this.activeProvider.localVideoPublisherId}`);
            if (videoElContainer) {
                videoEl = videoElContainer.querySelector('video');
            }
        }
        else if (currentStreamId === 'screen') {
            // FIXME:
            const videoElContainer = document.querySelector(`#${this.activeProvider.localScreenPublisherId}`);
            if (videoElContainer) {
                videoEl = videoElContainer.querySelector('video');
            }
        }
        else {
            const currentStream = this.activeProvider.getStreamById(currentStreamId);
            if (!currentStream) {
                return null;
            }
            videoEl = await currentStream.videoElementPromise;
        }
        return videoEl;
    }
    pin(streamId) {
        if (!this.activeProvider.isInCall) {
            return;
        }
        if (!this.canPin()) {
            this.update({ pinnedStreamId: streamId });
            this.emit('pin');
            return;
        }
        if (this.globalUi.ui.pinnedStreamId === streamId) {
            const screenShareStreamId = this.getScreenShareStreamId();
            const nextPinnedStreamId = streamId === screenShareStreamId ? null : screenShareStreamId;
            this.update({ pinnedStreamId: nextPinnedStreamId });
        }
        else {
            this.update({ pinnedStreamId: streamId });
        }
        this.emit('pin');
    }
    update(data) {
        const activeStreamId = data.activeStreamId;
        const pinnedStreamId = data.pinnedStreamId;
        const previousActiveStreamId = this.globalUi.ui.activeStreamId;
        const beforeStreamId = this.getCurrentStreamId();
        if (pinnedStreamId !== undefined) {
            this.globalUi.ui.pinnedStreamId = pinnedStreamId;
        }
        if (activeStreamId !== undefined) {
            this.globalUi.ui.activeStreamId = activeStreamId;
        }
        const afterStreamId = this.getCurrentStreamId();
        if (beforeStreamId !== afterStreamId) {
            this.$log.debug('active-stream-update', beforeStreamId, afterStreamId);
            this.emit('update');
        }
        if (previousActiveStreamId !== activeStreamId) {
            const currentStream = this.activeProvider.getStreamById(activeStreamId);
            if (currentStream) {
                const lastActivity = Date.now();
                currentStream.lastActivity = lastActivity;
                this.$ngRedux.dispatch(updateParticipant(activeStreamId, { lastActivity }));
            }
        }
    }
    onStreamDestroy(streamId) {
        if (this.globalUi.ui.activeStreamId === streamId &&
            Boolean(this.activeProvider.streams[0])) {
            this.update({ activeStreamId: this.activeProvider.streams[0].id });
        }
        if (this.globalUi.ui.pinnedStreamId === streamId) {
            this.update({ pinnedStreamId: null });
        }
    }
    canPin() {
        return (this.activeProvider.streams.length > 1 || this.globalUi.ui.screenSharing);
    }
    isPinned(streamId) {
        return this.globalUi.ui.pinnedStreamId === streamId && this.canPin();
    }
    isFull(streamId) {
        if (this.globalUi.ui.modalOpen) {
            return false;
        }
        if (!this.activeProvider.isInCall) {
            return false;
        }
        if (this.globalUi.ui.pinnedStreamId === streamId) {
            return true;
        }
        if (!this.globalUi.ui.pinnedStreamId &&
            this.globalUi.ui.activeStreamId === streamId) {
            return true;
        }
        return false;
    }
    shouldAddWrapperHidden(streamId) {
        return this.isFull(streamId) && !this.canPin();
    }
    setCurrentStreamByVolume() {
        if (this.activeProvider.streams.length === 0) {
            return;
        }
        if (this.globalUi.ui.pinnedStreamId &&
            !this.isStreamExists(this.globalUi.ui.pinnedStreamId)) {
            this.update({ pinnedStreamId: null });
        }
        if (this.activeProvider.streams.length === 1) {
            this.update({ activeStreamId: this.activeProvider.streams[0].id });
            return;
        }
        const streams = _.filter(this.activeProvider.streams, {
            hasAudio: true,
        });
        if (streams.length > 0) {
            const highVolumeStream = _.last(_.sortBy(streams, (c) => c.volumeLevel));
            // Try threshold (20%) to avoid it being too jumpy
            if (highVolumeStream.volumeLevel < this.ACTIVE_STREAM_VOLUME_THRESHOLD) {
                return;
            }
            const isThereAnotherStreamWithTheSameVolume = streams.filter((c) => c.volumeLevel === highVolumeStream.volumeLevel)
                .length > 1;
            if (!isThereAnotherStreamWithTheSameVolume) {
                const pinnedStreamId = this.globalUi.ui.pinnedStreamId;
                if (pinnedStreamId) {
                    this.update({
                        activeStreamId: highVolumeStream.id,
                        pinnedStreamId: this.isStreamExists(pinnedStreamId)
                            ? pinnedStreamId
                            : null,
                    });
                }
                else {
                    this.update({ activeStreamId: highVolumeStream.id });
                }
                return;
            }
        }
        // set first by default
        const isActiveStreamRemoved = !this.isStreamExists(this.globalUi.ui.activeStreamId);
        if (!this.globalUi.ui.activeStreamId || isActiveStreamRemoved) {
            this.update({ activeStreamId: this.activeProvider.streams[0].id });
        }
    }
    isStreamExists(streamId) {
        return (['self', 'screen'].includes(streamId) ||
            _.some(this.activeProvider.streams, {
                id: streamId,
            }));
    }
    isScreenShareActive() {
        return (this.globalUi.ui.screenSharing || Boolean(this.getScreenShareStreamId()));
    }
    getScreenShareStreamId() {
        const screenShareStream = this.activeProvider.streams.find((stream) => stream.type === StreamType.Screen);
        return screenShareStream ? screenShareStream.id : null;
    }
    onScreenSharingActivate() {
        this.updateGalleryViewState();
    }
    onScreenSharingDeactivate() {
        this.updateGalleryViewState();
    }
    onParticipantsCountChanged() {
        this.updateGalleryViewState();
    }
    updateGalleryViewState() {
        const participantsCount = this.activeProvider.streams.length;
        const isScreenShareActive = this.isScreenShareActive();
        if (participantsCount > this.MAX_PATIENTS_IN_GALLERY_VIEW ||
            isScreenShareActive ||
            this.globalUi.ui.isShowingWhiteboard) {
            if (this.globalUi.ui.isGalleryViewActivated) {
                this.isReturnToGalleryView = true;
                this.globalUi.ui.isGalleryViewActivated = false;
                this.$rootScope.$emit('galleryViewStatusChanged', this.globalUi.ui.isGalleryViewActivated);
            }
            window.localStorage.setItem('is-return-to-gallery-view', String(this.isReturnToGalleryView));
            this.globalUi.ui.isGalleryViewButtonHidden = true;
        }
        if (participantsCount <= this.MAX_PATIENTS_IN_GALLERY_VIEW &&
            !isScreenShareActive &&
            !this.globalUi.ui.isShowingWhiteboard) {
            if (this.isReturnToGalleryView) {
                this.isReturnToGalleryView = false;
                this.globalUi.ui.isGalleryViewActivated = true;
                this.$rootScope.$emit('galleryViewStatusChanged', this.globalUi.ui.isGalleryViewActivated);
            }
            this.globalUi.ui.isGalleryViewButtonHidden = false;
        }
    }
}
export default angular
    .module('doxy.providers.video.common.services.activeStreamService', [])
    .service('activeStreamService', ActiveStreamService).name;
